import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";

// Styles and components from Explore V2 Strategy Page
import {
  PageWrapper,
  ChartCard,
  ContentGrid,
  RightSidebarWrapper,
  PastPerformanceDisclaimer,
} from "shared/StrategyOverview/v2/Components/StrategyPageV2.styles";
import { Text } from "style/v2/components/Typography";
import BuildStrategy from "shared/StrategyOverview/v2/Components/BuildStrategy";
import News from "features/Research/v2/components/News";
import StrategyTreemap from "shared/StrategyTreemap";
import SimilarStrategies from "shared/StrategyOverview/v2/Components/SimilarStrategies";
import TopHoldings from "shared/StrategyOverview/v2/Components/Tables/TopHoldings";
import TopHoldingsDetail from "shared/StrategyOverview/v2/Components/Tables/TopHoldingsDetail";
import YourActiveHoldings from "shared/StrategyOverview/v2/Components/Tables/YourActiveHoldings";
import RecentTransactions from "shared/StrategyOverview/v2/Components/Tables/RecentTransactions";
import HeroCard from "shared/StrategyOverview/v2/Components/HeroCard";
import HeroCardSkeleton from "shared/StrategyOverview/v2/Components/HeroCard/HeroCardSkeleton";
import HeroCardError from "shared/StrategyOverview/v2/Components/HeroCard/HeroCardError";
import ReturnValue from "features/Dashboard/v2/Components/AccountDetails/ReturnValue";
import StrategyChart from "shared/StrategyOverview/v2/Components/StrategyChart";

// Utils and hooks
import { getStockCompanyIcon } from "utils/index";
// import { isFreeUser } from "utils/index";
import { useStrategyDetails } from "features/Strategy/hook/useStrategyDetails";
// import { useStrategyReturns } from "hooks/useStrategyReturns";
import { default as KeyStats } from "features/Explore/v2/Components/KeyStats";
import AvailableBrokersCard from "features/Explore/v2/Components/AvailableBrokersCard";
import InvestmentOrderCard from "features/Explore/v2/Components/InvestmentOrderCard";
// import { getHPerformanceData } from "features/Dashboard/utils";
import { scrollTopValueSelect } from "Layout/slice/selector";
import {
  setBreadcrumbDetailName,
  clearBreadcrumbDetailName,
} from "Layout/slice";
import PublicAccessCTA from "shared/StrategyOverview/v2/Components/PublicAccessCTA";
import { useNavigation } from "utils/navigate";
import { StrategyShareModal } from "shared/Modal/StrategyShareModal";
import {
  filterChartByTimeframe,
  getReturnSummary,
  getStrategyChartByKey,
  normalizeChartData,
} from "utils/charts";
import { TIME_INDICATOR, getStrategyDisclosure } from "utils/constants";
import { trackEvent } from "utils/tracking";
import { parseDateISO, DATE_FORMAT } from "utils/date";
import { format as formatDate } from "date-fns";

export const CHART_VALUE_KEY = "Strategy Value";

const StrategyOverviewPageV2 = ({ isPublicStrategy = false }) => {
  const dispatch = useDispatch();
  const scrollTopValue = useSelector(scrollTopValueSelect);
  const { id } = useParams();
  const { navigateToV2Signup, navigateToV2Signin } = useNavigation();
  const [showCta, setShowCta] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    data: strategyDetailData,
    isLoading: isDetailsLoading,
    error: detailsError,
  } = useStrategyDetails(id, isPublicStrategy);

  // const { data: strategyReturnsData, isLoading: isReturnsLoading } =
  //   useStrategyReturns(id);

  // Public or free plan should see blurred content + lock modal
  // const isFreePlan = isFreeUser();
  const isHiddenContent = isPublicStrategy;

  // const hasReturnData = useMemo(() => {
  //   const hasToday = Boolean(
  //     strategyReturnsData?.todays_return?.amount ||
  //       strategyReturnsData?.todays_return?.percentage ||
  //       (Array.isArray(strategyReturnsData?.todays_return_chart) &&
  //         strategyReturnsData?.todays_return_chart?.length > 0)
  //   );
  //   const hasNet = Boolean(
  //     strategyReturnsData?.net_return?.amount ||
  //       strategyReturnsData?.net_return?.percentage ||
  //       (Array.isArray(strategyReturnsData?.net_return_chart) &&
  //         strategyReturnsData?.net_return_chart?.length > 0)
  //   );
  //   return hasToday || hasNet;
  // }, [strategyReturnsData]);

  // Memoize data
  const treemapData = useMemo(() => {
    return strategyDetailData?.graphs?.industries || {};
  }, [strategyDetailData?.graphs?.industries]);

  const heroCardData = useMemo(() => {
    const getStatValue = (name) =>
      strategyDetailData?.key_stats?.find((s) => s.name === name)?.value ?? "";

    const periodReturn1Y = strategyDetailData?.period_returns?.["1Y"];

    return {
      id: strategyDetailData?.strategy_id,
      title: strategyDetailData?.name || "",
      authorName: strategyDetailData?.author_name || "--",
      authorId: strategyDetailData?.author_id,
      description: strategyDetailData?.description || "--",
      coverPhoto:
        strategyDetailData?.cover_photo ||
        strategyDetailData?.strategy_image ||
        "/src/assets/images/PlanCardBg1.png",
      investment: strategyDetailData?.investment?.formatted,
      industry: strategyDetailData?.graphs?.top_industry || "--",
      topHoldings:
        strategyDetailData?.graphs?.top_holdings
          ?.slice(0, 4)
          ?.map((symbol) => getStockCompanyIcon(symbol?.name || "")) ?? [],
      assetType: getStatValue("Asset Classes"),
      annualReturn: periodReturn1Y
        ? `${(periodReturn1Y * 100).toFixed(2)}%`
        : "",
      risk: strategyDetailData?.risk_score,
      disclosure: getStrategyDisclosure(
        strategyDetailData?.disclosure_type,
        strategyDetailData?.disclosure_subject
      ),
    };
  }, [strategyDetailData]);

  useEffect(() => {
    if (id && strategyDetailData?.name) {
      trackEvent({
        name: "view_strategy_details",
        label: "Strategy",
        strategy_id: id,
        strategy_name: strategyDetailData.name,
      });
    }
  }, [id, strategyDetailData?.name]);

  // breadcrumb helper
  useEffect(() => {
    if (heroCardData?.title) {
      dispatch(setBreadcrumbDetailName(heroCardData.title));
    }
    return () => {
      dispatch(clearBreadcrumbDetailName());
    };
  }, [heroCardData?.title]);

  // Toggle CTA visibility when user scrolls past chart and near the bottom
  useEffect(() => {
    if (!isHiddenContent) return;

    const chartElement = document.querySelector(".historical-chart-card");
    if (!chartElement) return;

    const chartRect = chartElement.getBoundingClientRect();
    const stickyHeight = 500;

    const isChartVisible =
      chartRect.top < window.innerHeight - stickyHeight &&
      chartRect.bottom > stickyHeight;

    const scrolledToBottom =
      window.innerHeight + scrollTopValue >
      document.documentElement.offsetHeight;

    const shouldShowCTA = !isChartVisible && scrolledToBottom;
    setShowCta(shouldShowCTA);
  }, [scrollTopValue, isHiddenContent]);

  const liveInceptionDate = useMemo(() => {
    const parsed = parseDateISO(strategyDetailData?.live_inception_date);
    return parsed ? formatDate(parsed, DATE_FORMAT) : "";
  }, [strategyDetailData?.live_inception_date]);

  const lastMonthSummary = useMemo(() => {
    const PERIOD = "1M";
    const threeMonthData = getStrategyChartByKey(
      strategyDetailData?.overview_chart || [],
      "3M" // from the backend as its not having key of 1M
    );
    const lastMonthData = filterChartByTimeframe(threeMonthData, PERIOD);
    const normalized = normalizeChartData(lastMonthData, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  const allTimeSummary = useMemo(() => {
    const PERIOD = "MAX";
    const filtered = filterChartByTimeframe(
      getStrategyChartByKey(strategyDetailData?.overview_chart || [], PERIOD),
      PERIOD
    );
    const normalized = normalizeChartData(filtered, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  return (
    <Row>
      <Col md={12} lg={9}>
        <PageWrapper>
          {isHiddenContent && (
            <Helmet>
              <meta name="robots" content="noindex, nofollow"></meta>
            </Helmet>
          )}
          {isDetailsLoading ? (
            <HeroCardSkeleton />
          ) : detailsError ? (
            <HeroCardError
              error={detailsError}
              onRetry={() => window.location.reload()}
            />
          ) : (
            <HeroCard
              data={heroCardData}
              onShareClick={() => {
                trackEvent({
                  name: "strategy_share",
                  label: "Strategy",
                  strategy_id: id,
                  strategy_name: strategyDetailData?.name,
                });
                setShowShareModal(true);
              }}
            />
          )}

          <div className="historical-chart-card">
            <ChartCard>
              <Text variant="cardHeading" weight="500" as="h3" mb="16px">
                Performance with Backtest
              </Text>
              <StrategyChart strategyId={id} isPublic={isPublicStrategy} />
              {!isDetailsLoading && strategyDetailData && (
                <PastPerformanceDisclaimer>
                  {liveInceptionDate
                    ? `Live Inception Date is ${liveInceptionDate}. Performance shown in this graph is based on backtested data.`
                    : "All performance shown in this graph is based on backtested data."}
                </PastPerformanceDisclaimer>
              )}
            </ChartCard>
          </div>
          <PastPerformanceDisclaimer>
            Past performance does not guarantee future results.
          </PastPerformanceDisclaimer>
          <Row>
            <Col md={6}>
              <ReturnValue title="Last month's return" {...lastMonthSummary} />
            </Col>
            <Col md={6}>
              <ReturnValue title="Total return" {...allTimeSummary} />
            </Col>
          </Row>

          <ContentGrid
            id="blur-content"
            className={`${isHiddenContent ? "blur-overlay" : ""}`}
          >
            <KeyStats
              stats={strategyDetailData?.key_stats || []}
              isLoading={isDetailsLoading}
              error={detailsError}
            />
            <YourActiveHoldings />
            {strategyDetailData?.graphs?.top_holdings?.length > 0 && (
              <TopHoldings
                data={strategyDetailData?.graphs?.top_holdings || []}
                isLoading={isDetailsLoading}
                error={detailsError}
                strategyId={id}
              />
            )}
            {(strategyDetailData?.graphs?.top_holdings_30d?.length > 0 ||
              strategyDetailData?.graphs?.top_holdings_average?.length > 0 ||
              strategyDetailData?.graphs?.top_holdings_current?.length > 0) && (
              <TopHoldingsDetail
                holdings30d={strategyDetailData?.graphs?.top_holdings_30d || []}
                holdingsAverage={
                  strategyDetailData?.graphs?.top_holdings_average || []
                }
                holdingsCurrent={
                  strategyDetailData?.graphs?.top_holdings_current || []
                }
                isLoading={isDetailsLoading}
              />
            )}
            {treemapData && (
              <StrategyTreemap
                data={treemapData}
                isLoading={isDetailsLoading}
                error={detailsError}
              />
            )}
            <RecentTransactions strategyId={id} />
            <News strategyId={id} />
            <SimilarStrategies strategyId={id} />
            <BuildStrategy />
          </ContentGrid>
          <StrategyShareModal
            showShareModal={showShareModal}
            toggleShareModal={() => setShowShareModal(false)}
            strategyDetail={strategyDetailData || {}}
            coverPhoto={heroCardData?.coverPhoto}
          />
          {isHiddenContent && (
            <PublicAccessCTA
              visible={showCta}
              onSignup={() => navigateToV2Signup()}
              onSignin={() => navigateToV2Signin()}
            />
          )}
        </PageWrapper>
      </Col>

      <Col md={12} lg={3}>
        <RightSidebarWrapper>
          <PageWrapper>
            <InvestmentOrderCard
              isPublicStrategy={isPublicStrategy}
              canInvest={strategyDetailData?.can_invest ?? true}
            />
            {strategyDetailData?.supported_brokers?.length > 0 && (
              <AvailableBrokersCard
                brokers={strategyDetailData?.supported_brokers || []}
                error={detailsError}
              />
            )}
          </PageWrapper>
        </RightSidebarWrapper>
      </Col>
    </Row>
  );
};

StrategyOverviewPageV2.propTypes = {
  isPublicStrategy: PropTypes.bool,
};

export default StrategyOverviewPageV2;
